import React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSmallSpinner from "../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaH4 from "../../ui-kit/typography/H4/H4";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { IStepScreenFormWizardContentProps, StepScreenFormWizardContent } from "./step-screen-form-wizard-content";
import { IStepScreenFormSidebarProps, StepScreenFormWizardSidebar } from "./step-screen-form-wizard-sidebar";

export interface IStepScreenFormWizardFormsTabProps
  extends IStepScreenFormSidebarProps, IStepScreenFormWizardContentProps {
  onClickCreateAllWorkflowScreens?: () => void;
  getStepsWithoutScreens?: () => { processId: string; stepId: string; processName: string; stepName: string }[];
  progressInfo?: {
    currentStep: number;
    totalSteps: number;
    currentProcessId?: string;
    currentProcessName?: string;
    currentStepId?: string;
    currentStepName?: string;
    completedSteps: any[];
    errors: { processId: string; stepId: string; error: string }[];
    isInitializing?: boolean;
    statusMessage?: string;
  } | null;
  isStepSelectionLoading?: boolean;
}

export interface IStepScreenFormWizardFormsTabState {}

class StepScreenFormWizardFormsTab extends React.PureComponent<
  IStepScreenFormWizardFormsTabProps & IWithSDProps,
  IStepScreenFormWizardFormsTabState
> {
  constructor(props: IStepScreenFormWizardFormsTabProps & IWithSDProps) {
    super(props);
    this.state = {};
  }

  /** What the panel says it is doing, in the caller's own words when it has some. */
  private getStatusMessage = (): string => {
    const progressInfo = this.props.progressInfo;

    if (!progressInfo) {
      return "";
    }

    if (progressInfo.statusMessage) {
      return progressInfo.statusMessage;
    }

    if (progressInfo.isInitializing) {
      return "Preparing to update workflow screens...";
    }

    return `Processing ${progressInfo.currentStep} of ${progressInfo.totalSteps} screens...`;
  };

  private renderProgressPanel = (): React.ReactNode => {
    const progressInfo = this.props.progressInfo;

    if (!progressInfo) {
      return null;
    }

    const percentDone: number =
      progressInfo.totalSteps > 0 ? Math.round((progressInfo.currentStep / progressInfo.totalSteps) * 100) : 0;
    const errorCount: number = progressInfo.errors.length;
    const hasCurrentStep: boolean = !!(progressInfo.currentProcessName || progressInfo.currentStepName);

    return (
      <div className="kuika-step-screen-progress">
        <div className="kuika-step-screen-progress__panel">
          <div className="kuika-step-screen-progress__headline">
            <KuikaSmallSpinner />
            <KuikaH4 className="kuika-step-screen-progress__title">Updating Workflow Screens</KuikaH4>
          </div>
          <KuikaText className="kuika-step-screen-progress__status">{this.getStatusMessage()}</KuikaText>

          <div className="kuika-step-screen-progress__track">
            {progressInfo.isInitializing ? (
              <div className="kuika-step-screen-progress__fill kuika-step-screen-progress__fill--indeterminate" />
            ) : (
              // The one genuinely computed value on this panel.
              <div className="kuika-step-screen-progress__fill" style={{ width: `${percentDone}%` }} />
            )}
          </div>

          {!progressInfo.isInitializing && hasCurrentStep && (
            <KuikaInfoBox
              color="gray"
              className="kuika-step-screen-progress__box"
              leftUnit={
                <span className="kuika-step-screen-progress__current">
                  <span className="kuika-step-screen-progress__current-label">Currently Processing</span>
                  <span className="kuika-step-screen-progress__current-step">
                    <strong>{progressInfo.currentProcessName || "Unknown Process"}</strong>
                    <span className="kuika-step-screen-progress__separator">›</span>
                    <span>{progressInfo.currentStepName || "Unknown Step"}</span>
                  </span>
                </span>
              }
            />
          )}

          {!progressInfo.isInitializing && errorCount > 0 && (
            <KuikaInfoBox
              color="red"
              className="kuika-step-screen-progress__box"
              leftUnit={
                <span className="kuika-step-screen-progress__errors">
                  <KuikaSvg name="alert-triangle" width={16} height={16} />
                  {errorCount} error{errorCount !== 1 ? "s" : ""} encountered
                </span>
              }
            />
          )}
        </div>
      </div>
    );
  };

  render() {
    const isProgressActive =
      !!this.props.progressInfo &&
      // Show as soon as streaming starts
      (this.props.progressInfo.isInitializing === true ||
        // Or when we have any totals/steps
        (this.props.progressInfo.totalSteps ?? 0) > 0 ||
        (this.props.progressInfo.currentStep ?? -1) >= 0);

    return (
      <div className="ssfw-body">
        {this.props.processes.length <= 0 ? (
          <div className="ssfw-no-processes">
            <span>No processes available</span>
          </div>
        ) : (
          <>
            {/* Only show sidebar when not processing */}
            {!isProgressActive && (
              <StepScreenFormWizardSidebar
                processes={this.props.processes}
                onClickGetStarted={() => this.props.onChangeSelectedStep?.(null, null)}
                getScreenForStep={this.props.getScreenForStep}
                getIsScreenManaged={this.props.getIsScreenManaged}
                draftSteps={this.props.draftSteps}
                pendingUnmanagedAssignments={this.props.pendingUnmanagedAssignments}
                selectedProcessId={this.props.selectedProcessId}
                selectedStepId={this.props.selectedStepId}
                onChangeSelectedStep={this.props.onChangeSelectedStep}
                reloadScreen={this.props.reloadScreen}
                removeDraftFormMetadata={this.props.removeDraftFormMetadata}
                setScreenUnmanaged={this.props.setScreenUnmanaged}
                untriggeredProcessIds={this.props.untriggeredProcessIds}
                onConfirmGenerateStartScreen={this.props.onConfirmGenerateStartScreen}
              />
            )}
            <div className="ssfw-content-container">
              {/* Show centered progress when bulk operation is active */}
              {isProgressActive && this.renderProgressPanel()}
              {/* Only render normal content when progress is not active */}
              {!isProgressActive && (
                <StepScreenFormWizardContent
                  isScreenDoesNotExist={this.props.isScreenDoesNotExist}
                  isScreenUnmanaged={this.props.isScreenUnmanaged}
                  formMetadata={this.props.formMetadata}
                  onMetadataChange={this.props.onMetadataChange}
                  selectedProcessId={this.props.selectedProcessId}
                  selectedStepId={this.props.selectedStepId}
                  onClickCreateWorkflowScreen={this.props.onClickCreateWorkflowScreen}
                  onClickCreateAllWorkflowScreens={this.props.onClickCreateAllWorkflowScreens}
                  getStepsWithoutScreens={this.props.getStepsWithoutScreens}
                  progressInfo={this.props.progressInfo}
                  isStepSelectionLoading={this.props.isStepSelectionLoading}
                  pendingUnmanagedAssignments={this.props.pendingUnmanagedAssignments}
                  pendingUnmanagedScreenId={this.props.pendingUnmanagedScreenId}
                  onChangePendingUnmanagedAssignment={this.props.onChangePendingUnmanagedAssignment}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

const stepScreenFormWizardFormsTab = withSD<IStepScreenFormWizardFormsTabProps>(StepScreenFormWizardFormsTab);
export { stepScreenFormWizardFormsTab as StepScreenFormWizardFormsTab };
